import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type { ClaimChartApi, ClaimChartRequest, ClaimChartResponse } from '../claim-chart-api';
import { GuestAwareHttpService } from '../../services/guest-aware-http-service';
import { formatTimeoutError } from '../../../utils/timeout-handler';
import { resolveApiPath } from '@/utils/api-path-resolver';
import { FactoryPsHttpSSE, type PsHttpSSE } from '@patsnap-utils/sse';
import { injectService } from '@patsnap/tahiti-core';
import { TrackService } from '@/domains/shared/services/track-service';

@injectable()
export class ClaimChartApiImpl extends GuestAwareHttpService implements ClaimChartApi {
    private trackTaskFinished() {
        const trackService = injectService<TrackService>(TrackService);
        trackService.trackAir({
            event_type: 'ss_telecom',
            source_type: 'task_finished',
            other_param: 'aicc',
            event_detail_str: JSON.stringify({
                agent: 'aicc',
                pageUrl: '/essentiality-claim/flow/chart',
                params: {},
            }),
        }).catch(error => {
            console.warn('[ClaimChartApi] Failed to track task_finished:', error);
        });
    }

    async getClaimChartStream(request: ClaimChartRequest): Promise<PsHttpSSE<ClaimChartResponse>> {
        const sseClient = injectService<FactoryPsHttpSSE>(FactoryPsHttpSSE);
        const resolvedPath = await resolveApiPath('cc_compare_sse');
        // 与 GuestAwareHttpService 保持一致：本地开发 baseURL 为空走 vite proxy，生产/QA 用 VITE_TELECOM_API
        const baseURL = import.meta.env.DEV ? '' : (import.meta.env.VITE_TELECOM_API || '');
        const url = `${baseURL}${resolvedPath}`;

        return sseClient<ClaimChartResponse>()
            .init({
                url,
                data: {
                    task_id: request.task_id,
                },
                hasFinalMessage: () => true,
            });
    }

    async getClaimChart(request: ClaimChartRequest): Promise<ClaimChartResponse> {
        try {
            console.debug(`[ClaimChartApi] Starting SSE request for task_id: ${request.task_id}`);

            let finalResult: ClaimChartResponse = {
                status: false,
                error_code: -1,
                error_msg: 'No data received from SSE',
                data: { compare_results: [] },
            };

            const stream = await this.getClaimChartStream(request);

            const result = await new Promise<ClaimChartResponse>((resolve, reject) => {
                stream
                    .onOpen(() => {
                        console.debug('[ClaimChartApi] SSE connection opened');
                    })
                    .onMessage((event) => {
                        // event: SSEResponse<ClaimChartResponse>
                        if (event.data.status === 'FINISH') {
                            finalResult = {
                                status: true,
                                error_code: 0,
                                error_msg: '',
                                data: { compare_results: event.data.content.compare_results || [] },
                            };
                        }
                    })
                    .onFinish(() => {
                        console.debug('[ClaimChartApi] SSE finished');
                        if (finalResult.status) {
                            this.trackTaskFinished();
                        }
                        resolve(finalResult);
                    })
                    .onError((err) => {
                        console.error('[ClaimChartApi] SSE error:', err);
                        reject(err);
                    })
                    .start()
                    .catch((err) => {
                        console.error('[ClaimChartApi] SSE start failed:', err);
                        reject(err);
                    });
            });

            console.debug('[ClaimChartApi] SSE request completed');
            return result;
        } catch (error) {
            const formattedError = formatTimeoutError(error, 'Claim Chart API');
            console.error('[ClaimChartApi] Request failed:', formattedError);
            throw new Error(formattedError);
        }
    }
}
